/* istanbul ignore file */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ISpecificationState } from "../types/specification-types";
import {
  ISpecificationCategoryResponse,
  ISpecificationItemResponse,
} from "@/shared/services/interface/responses/ISpecificationResponse";
import {
  ISupabaseSpecificationCategory,
  ISupabaseSpecificationItem,
} from "@/shared/services/interface/responses/ISupabaseSpecification";
import {
  addCategoryToCategories,
  convertAllCategoriesToSupabaseFormat,
  convertAllItemsToSupabaseFormat,
  findCategoryById,
  getAllCategoriesAndSubCategories,
  getAllItems,
  removeCategoryRecursively,
  removeItemRecursively,
  removeItemByIdFromAllCategories,
  updateCategoryRecursively,
  updateCategoryNameRecursively,
  updateItemRecursively,
  categoryMatchesSearch,
  itemMatchesSearch,
} from "../helper/specification";
import {
  getAllCategoryNames,
  getItemsPriority,
} from "@/shared/helpers/specification-helper";
import {
  getCategoryAndItemThunk,
  getSpecificationThunk,
  getProductCategoriesThunk,
} from "../thunks/specificationThunk";
import { SpecificationItemAnswerStatus } from "@/shared/enums/specification-item-answer";
import { ISidebarFilter } from "../types/specification-types";
import { performanceMonitor } from "@/shared/utils/performance-monitor";

const initialSidebarFilter: ISidebarFilter = {
  answerStatuses: [],
  showConflicts: false,
};

const initialState: ISpecificationState = {
  skeletonLoading: true,
  specificationLoading: false,
  specificationError: null,
  changedValues: {
    mainCategories: [],
    subCategories: [],
    items: [],
  },
  thresHoldData: null,
  specification: {
    specification: {
      name: "",
      company: {
        id: "",
        createdAt: "",
        updatedAt: "",
        deletedAt: null,
        name: "",
        website: null,
        logoUrl: null,
        isActive: false,
        status: "",
        countryId: null,
        sectorId: null,
        languageId: null,
        regionId: null,
        ownerId: null,
      },
      id: "",
      createdAt: "",
      updatedAt: "",
      deletedAt: null,
      discoveryAt: "",
      status: 0,
      shareLink: {
        id: "",
        createdAt: "",
        updatedAt: "",
        deletedAt: null,
        specificationId: "",
        hash: "",
        isAccessAllCategories: false,
        isPublic: false,
        permission: "",
        categories: [],
        expiresAt: null,
      },
      folderId: null,
      templateSpecificationId: null,
      isSendToSupplier: false,
      isTitleGenerated: false,
      screens: null,
      specificationType: "manual",
      specType: null,
      regionId: null,
      categoryNames: {
        categoryList: [],
        selectedCategory: null,
      },
      isReadyForProductExplore: false,
      itemCount: 0,
      position: "",
      tooltip: null,
      shareLinkId: null,
      tips: {
        lastUpdated: "",
        thresholdTips: [],
      },
      region: null,
    },
    createdGeneralCategory: {
      name: "",
      specification: {
        id: "",
      },
      parent: {
        id: "",
      },
      id: "",
      createdAt: "",
      updatedAt: "",
      deletedAt: null,
      isActive: true,
    },
  },
  categories: [],
  filteredCategories: [],
  allCategoryNames: [],
  activeSpecificationStep: {
    id: "",
    type: "item" as const,
    description: "",
    score: 0,
    position: "",
    category: {
      id: "",
    },
    createdAt: "",
    updatedAt: "",
    deletedAt: null,
    isActive: true,
    orderNo: null,
    priority: "",
    isNew: false,
  },
  totalSpecification: 0,
  openCategories: [],
  expandedItems: [],
  loading: false,
  error: null,
  currentItemIndex: 0,
  totalItems: 0,
  searchTerm: "",
  openSidebar: true,
  isInputFocused: false,
  isNewRequirementInputFocused: false,
  notNewItemsCount: 0,
  aiPopover: {
    placeHolders: [],
    selectedPlaceHolder: "",
  },
  productCategories: {
    data: null,
    loading: false,
    error: null,
  },
  isSpecificationCreateSidebarOpen: false,
  dashboardSidebarWidth: 320, // Default width in pixels (matches other sidebars)
  highlightedCategoryIds: [], // Category IDs that should be highlighted (recently updated via streaming)
  sidebarFilter: initialSidebarFilter,
};

// Helper fonksiyon - slice dışında da tanımlanabilir
const convertProxyToPlain = (proxyObject: any) => {
  try {
    return JSON.parse(JSON.stringify(proxyObject));
  } catch (error) {
    console.warn("Proxy to plain conversion failed:", error);
    return proxyObject;
  }
};

const resolveIsNewForActiveStep = (
  state: ISpecificationState,
  itemId?: string
) => {
  if (!itemId) {
    return true;
  }

  return state.changedValues.items.some(
    (item) => item.id === itemId && item.isNew
  );
};

export const specificationSlice = createSlice({
  name: "specification",
  initialState,
  reducers: {
    setInitialSpecification: (state) => {
      state.specification.specification.status = 0;
      state.specification.specification =
        initialState.specification.specification;
    },

    //AI Popover
    setAiPopover: (state, action) => {
      state.aiPopover = action.payload;
    },
    setPlaceHolders: (state, action) => {
      state.aiPopover.placeHolders = action.payload;
    },
    setSelectedPlaceHolder: (state, action) => {
      state.aiPopover.selectedPlaceHolder = action.payload;
    },
    setChangedCategories: (state, action) => {
      state.changedValues.mainCategories = action.payload.mainCategories;
      state.changedValues.subCategories = action.payload.subCategories;
    },
    setChangedItems: (state, action) => {
      state.changedValues.items = action.payload;
    },
    addChangedItem: (state, action) => {
      state.changedValues.items = [
        ...state.changedValues.items,
        action.payload,
      ];
    },
    removeChangedItem: (state, action) => {
      state.changedValues.items = state.changedValues.items.filter(
        (item) => item.id !== action.payload
      );
    },
    setIsInputFocused: (state, action) => {
      state.isInputFocused = action.payload;
    },
    setThresHoldData: (state, action) => {
      state.thresHoldData = action.payload;
    },
    setIsNewRequirementInputFocused: (state, action) => {
      state.isNewRequirementInputFocused = action.payload;
    },
    setSpecification: (state, action) => {
      state.specification = action.payload;
    },

    setSpecificationDetail: (state, action) => {
      state.specification.specification = action.payload;
    },
    setCategories: (
      state,
      action: PayloadAction<ISpecificationCategoryResponse[]>
    ) => {
      const response = action.payload;

      // ✅ Tek source of truth: sadece categories'i güncelle
      state.categories = response;

      // ✅ Keep filteredCategories same as categories - filtering happens in rendering
      state.filteredCategories = response;

      // ✅ Derived/computed değerler
      const allItems = getAllItems(response);
      state.allCategoryNames = getAllCategoryNames(response);
      state.totalItems = allItems.length;
      state.totalSpecification = state.totalItems;
      state.notNewItemsCount = allItems.filter((item) => !item.isNew).length;

      // ❌ changedValues'ı burada güncelleme
      // changedValues sadece Supabase subscription'lar için kullanılıyor
      // ve setMainAndSubCategoriesAndItems tarafından yönetiliyor
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
      // Don't filter categories here - filtering happens in rendering
      // Keep filteredCategories same as categories to preserve indices
      state.filteredCategories = state.categories;
    },
    setOpenSidebar: (state, action) => {
      state.openSidebar = action.payload;
    },
    setNameSpecification: (state, action) => {
      state.specification.specification.name = action.payload;
    },
    setactiveSpecificationStep: (state, action) => {
      state.activeSpecificationStep.description = action.payload;
    },
    setActiveSpeciticationByItemId: (state, action) => {
      // Proxy revoke sorununu çözmek için plain objeye dönüştür
      const filteredCategoriesPlain = convertProxyToPlain(
        state.filteredCategories
      );
      const allItems = getAllItems(filteredCategoriesPlain);
      const item = allItems.find((item) => item.id === action.payload);
      if (item) {
        state.activeSpecificationStep = { ...item };
        const priority = getItemsPriority(filteredCategoriesPlain, item);
        state.activeSpecificationStep.priority = priority.priority;
        if (
          state.activeSpecificationStep.orderNo === undefined ||
          state.activeSpecificationStep.orderNo === null ||
          state.activeSpecificationStep.orderNo === ""
        ) {
          state.activeSpecificationStep.orderNo = priority.priority;
        }
        state.activeSpecificationStep.isNew = resolveIsNewForActiveStep(
          state,
          state.activeSpecificationStep.id
        );
        state.currentItemIndex =
          allItems.findIndex((item) => item.id === action.payload) + 1;
      }
    },
    setActiveSpecificationItem: (state, action) => {
      const previousActiveStep = state.activeSpecificationStep;
      const isSameItem = previousActiveStep.id === action.payload.id;
      state.activeSpecificationStep = { ...action.payload };
      // Proxy revoke sorununu çözmek için plain objeye dönüştür
      const filteredCategoriesPlain = convertProxyToPlain(
        state.filteredCategories
      );
      const allItems = getAllItems(filteredCategoriesPlain);
      const priority = getItemsPriority(filteredCategoriesPlain, action.payload);

      const resolvedPriority =
        priority.priority ||
        state.activeSpecificationStep.priority ||
        (isSameItem ? previousActiveStep.priority : "") ||
        "";
      state.activeSpecificationStep.priority = resolvedPriority;
      if (
        state.activeSpecificationStep.orderNo === undefined ||
        state.activeSpecificationStep.orderNo === null ||
        state.activeSpecificationStep.orderNo === ""
      ) {
        state.activeSpecificationStep.orderNo =
          resolvedPriority ||
          (isSameItem ? previousActiveStep.orderNo : "") ||
          "";
      }
      state.activeSpecificationStep.isNew = resolveIsNewForActiveStep(
        state,
        state.activeSpecificationStep.id
      );

      state.currentItemIndex =
        allItems.findIndex((item) => item.id === action.payload.id) + 1;
    },
    setCurrentItemIndex: (state, action) => {
      state.currentItemIndex = action.payload;
    },
    setActiveEmptySpecification: (state, action) => {
      if (!state.activeSpecificationStep.category) {
        state.activeSpecificationStep.category = { id: "" };
      }
      state.activeSpecificationStep.category.id = action.payload.categoryId;
      state.activeSpecificationStep.isNew = true;
      state.activeSpecificationStep.createdAt = "";
      state.activeSpecificationStep.updatedAt = "";
      state.activeSpecificationStep.deletedAt = null;
      state.activeSpecificationStep.score = 5;
      state.activeSpecificationStep.orderNo = 0;
      state.activeSpecificationStep.id = "";
      state.activeSpecificationStep.description = "";
      state.activeSpecificationStep.priority = "";
    },
    setActiveInitialSpecification: (state) => {
      state.activeSpecificationStep = initialState.activeSpecificationStep;
    },
    setActiveCategoryId: (state, action) => {
      if (!state.activeSpecificationStep.category) {
        state.activeSpecificationStep.category = { id: "" };
      }
      state.activeSpecificationStep.category.id = action.payload;
    },
    setOpenCategories: (state, action) => {
      const existCategory = state.openCategories.find(
        (id) => id === action.payload
      );
      if (!existCategory) {
        // Kategoriyi ekle
        state.openCategories = [...state.openCategories, action.payload];
        // Ana kategoriyi bul ve ekle
        // Proxy revoke sorununu çözmek için plain objeye dönüştür
        const filteredCategoriesPlain = convertProxyToPlain(
          state.filteredCategories
        );

        // Güvenlik kontrolü ekle
        if (!filteredCategoriesPlain || filteredCategoriesPlain.length === 0) {
          console.warn("filteredCategories boş veya tanımsız!");
          return;
        }

        const category = findCategoryById(
          filteredCategoriesPlain,
          action.payload
        );

        // Note: parent property no longer exists, skipping parent category check
      } else {
        state.openCategories = state.openCategories.filter(
          (id) => id !== action.payload
        );
      }
    },
    setOpenCategoriesCollapse: (state, action) => {
      if (action.payload) {
        const categoryIDS: string[] = [];
        state.categories.forEach((category) => {
          categoryIDS.push(category.id);
        });
        state.openCategories = categoryIDS;
      } else {
        state.openCategories = [];
      }
    },
    setAllOpenCategories: (state, action) => {
      state.openCategories = action.payload;
    },
    resetOpenCategories: (state) => {
      state.openCategories = [];
    },
    // Add category IDs to highlight (for streaming updates)
    addHighlightedCategoryIds: (state, action: PayloadAction<string[]>) => {
      const newIds = action.payload.filter(
        (id) => !state.highlightedCategoryIds.includes(id)
      );
      if (newIds.length > 0) {
        state.highlightedCategoryIds = [...state.highlightedCategoryIds, ...newIds];
      }
    },
    // Remove category IDs from highlight
    removeHighlightedCategoryIds: (state, action: PayloadAction<string[]>) => {
      state.highlightedCategoryIds = state.highlightedCategoryIds.filter(
        (id) => !action.payload.includes(id)
      );
    },
    // Clear all highlighted category IDs
    clearHighlightedCategoryIds: (state) => {
      state.highlightedCategoryIds = [];
    },
    // Expand categories by IDs (add to openCategories without toggling)
    expandCategoryIds: (state, action: PayloadAction<string[]>) => {
      const newIds = action.payload.filter(
        (id) => !state.openCategories.includes(id)
      );
      if (newIds.length > 0) {
        state.openCategories = [...state.openCategories, ...newIds];
      }
    },
    resetChangedValues: (state) => {
      state.changedValues = initialState.changedValues;
      state.filteredCategories = initialState.filteredCategories;
    },
    // Expanded Items Management
    setExpandedItems: (state, action) => {
      const itemId = action.payload;
      const existItem = state.expandedItems.find((id) => id === itemId);
      if (!existItem) {
        state.expandedItems = [...state.expandedItems, itemId];
      } else {
        state.expandedItems = state.expandedItems.filter((id) => id !== itemId);
      }
    },
    setAllExpandedItems: (state, action) => {
      state.expandedItems = action.payload;
    },
    resetExpandedItems: (state) => {
      state.expandedItems = [];
    },
    navigateItems: (state, action: PayloadAction<"up" | "down">) => {
      if (state.currentItemIndex === 0 && action.payload === "up") return;
      // Tüm öğeleri recursive olarak al
      // Proxy revoke sorununu çözmek için plain objeye dönüştür
      const categoriesPlain = convertProxyToPlain(state.categories);
      const allItems = getAllItems(categoriesPlain);
      state.notNewItemsCount = allItems.filter((item) => !item.isNew).length;
      if (
        state.currentItemIndex === allItems.length &&
        action.payload === "down"
      )
        return;

      const currentIndex = state.currentItemIndex - 1;

      let newIndex;
      if (action.payload === "up") {
        newIndex = currentIndex <= 0 ? allItems.length - 1 : currentIndex - 1;
      } else {
        newIndex = currentIndex >= allItems.length - 1 ? 0 : currentIndex + 1;
      }

      const item = allItems[newIndex];
      const priority = getItemsPriority(categoriesPlain, item);
      state.activeSpecificationStep = { ...item };
      state.openCategories = [
        ...state.openCategories,
        ...priority.categoryPath,
      ];
      state.activeSpecificationStep.isNew = resolveIsNewForActiveStep(
        state,
        state.activeSpecificationStep.id
      );
      state.activeSpecificationStep.priority = priority.priority;
      state.currentItemIndex = newIndex + 1;
    },
    selectFirstItem: (state) => {
      const filteredCategoriesPlain = convertProxyToPlain(
        state.filteredCategories
      );
      const allItems = getAllItems(filteredCategoriesPlain);
      if (allItems.length > 0) {
        const firstItem = allItems[0];
        const priority = getItemsPriority(filteredCategoriesPlain, firstItem);
        state.activeSpecificationStep = firstItem;
        state.activeSpecificationStep.priority = priority.priority;
        // Use Set to avoid duplicates (setOpenCategories toggles, so duplicates cause unwanted closing)
        const openSet = new Set([
          ...state.openCategories,
          ...priority.categoryPath,
        ]);
        state.openCategories = Array.from(openSet);
        state.currentItemIndex = 1;
      }
    },
    setFilteredCategories: (state, action) => {
      state.filteredCategories = action.payload;
    },

    setMainAndSubCategories: (state) => {
      const mainCat = state.changedValues.mainCategories;
      const subCat = state.changedValues.subCategories;

      // Ana kategorileri setRealTimeCategory mantığıyla güncelle
      mainCat.forEach((categoryResponse: ISupabaseSpecificationCategory) => {
        const category = state.categories.find(
          (category) => category.id === categoryResponse.id
        );

        if (category) {
          // Mevcut kategoriyi güncelle
          category.name = categoryResponse.name;
          // Note: isActive, updatedAt, deletedAt properties no longer exist in base type
        } else {
          // Yeni kategori ekle
          const newCategory: ISpecificationCategoryResponse = {
            id: categoryResponse.id,
            type: "category",
            name: categoryResponse.name,
            position: "", // Default position
            children: null,
          };
          state.categories = [...state.categories, newCategory];
        }
      });

      // Alt kategorileri setRealTimeSubCategory mantığıyla güncelle
      subCat.forEach((subCategoryResponse: ISupabaseSpecificationCategory) => {
        state.categories = updateCategoryRecursively(
          state.categories,
          subCategoryResponse
        );
      });

      // ✅ Keep filteredCategories same as categories - filtering happens in rendering
      state.filteredCategories = state.categories;
      state.allCategoryNames = getAllCategoryNames(state.categories);
      // state.openCategories = [
      //   ...mainCat.map((category) => category.id),
      //   ...subCat.map((category) => category.id)
      // ];
      state.currentItemIndex = 0;
      state.totalSpecification = state.totalItems;
    },

    setItemsSupabase: (state) => {
      const items = state.changedValues.items;

      items.forEach((itemResponse: ISupabaseSpecificationItem) => {
        state.categories = updateItemRecursively(
          state.categories,
          itemResponse
        );
      });

      const allItems = getAllItems(state.categories);

      state.currentItemIndex = 0;
      state.totalItems = allItems.length;
      state.totalSpecification = state.totalItems;
    },
    updateItem: (state, action) => {
      state.categories = updateItemRecursively(
        state.categories,
        action.payload
      );
    },
    setMainAndSubCategoriesAndItems: (state) => {
      // Filter out deleted categories from changedValues
      const mainCat = state.changedValues.mainCategories.filter(
        (cat: ISupabaseSpecificationCategory) => !cat.deletedAt
      );
      const subCat = state.changedValues.subCategories.filter(
        (cat: ISupabaseSpecificationCategory) => !cat.deletedAt
      );
      const items = state.changedValues.items;

      const allCategories: string[] = [];

      // 🚨 PERFORMANCE MONITORING: Track the problematic forEach loop
      performanceMonitor.startTiming("mainCat-forEach-loop", {
        categoriesCount: mainCat.length,
        currentCategoriesCount: state.categories.length,
      });

      // Ana kategorileri setRealTimeCategory mantığıyla güncelle
      // Önce silinmiş kategorileri state'den kaldır
      const deletedCategoryIds = mainCat
        .filter((cat: ISupabaseSpecificationCategory) => cat.deletedAt)
        .map((cat: ISupabaseSpecificationCategory) => cat.id);

      if (deletedCategoryIds.length > 0) {
        state.categories = state.categories.filter(
          (cat) => !deletedCategoryIds.includes(cat.id)
        );
      }

      // Sadece silinmemiş kategorileri işle
      const nonDeletedMainCat = mainCat.filter(
        (cat: ISupabaseSpecificationCategory) => !cat.deletedAt
      );

      nonDeletedMainCat.forEach(
        (categoryResponse: ISupabaseSpecificationCategory) => {
          // 🐌 PERFORMANCE ISSUE: This .find() runs O(n) for each category, making this O(n²)
          const category = state.categories.find(
            (category) => category.id === categoryResponse.id
          );

          if (category) {
            // Mevcut kategoriyi güncelle
            category.name = categoryResponse.name;
            // Note: isActive, updatedAt, deletedAt properties no longer exist in base type
          } else {
            // 🚨 PERFORMANCE ISSUE: Creating new arrays on each iteration causes memory churn
            // Yeni kategori ekle
            const newCategory: ISpecificationCategoryResponse = {
              id: categoryResponse.id,
              type: "category",
              name: categoryResponse.name,
              position: "",
              children: null,
            };
            state.categories = [...state.categories, newCategory];
          }

          allCategories.push(categoryResponse.id);
        }
      );

      const forEachDuration = performanceMonitor.endTiming(
        "mainCat-forEach-loop"
      );

      // 🚨 Log warning if this operation is too slow
      if (forEachDuration && forEachDuration > 50) {
        console.warn(
          `🐌 SLOW PERFORMANCE: mainCat forEach took ${forEachDuration.toFixed(
            2
          )}ms with ${mainCat.length} categories`
        );
      }

      // 🚨 PERFORMANCE MONITORING: Track subCategory forEach loop
      performanceMonitor.startTiming("subCat-forEach-loop", {
        subCategoriesCount: subCat.length,
      });

      // Alt kategorileri setRealTimeSubCategory mantığıyla güncelle
      subCat.forEach((subCategoryResponse: ISupabaseSpecificationCategory) => {
        // 🚨 PERFORMANCE ISSUE: Recursive function called for each subcategory
        state.categories = updateCategoryRecursively(
          state.categories,
          subCategoryResponse
        );

        allCategories.push(subCategoryResponse.id);
      });

      const subCatDuration = performanceMonitor.endTiming(
        "subCat-forEach-loop"
      );
      if (subCatDuration && subCatDuration > 50) {
        console.warn(
          `🐌 SLOW PERFORMANCE: subCat forEach took ${subCatDuration.toFixed(
            2
          )}ms with ${subCat.length} subcategories`
        );
      }

      // Filter out deleted items from changedValues
      const nonDeletedItems = items.filter(
        (item: ISupabaseSpecificationItem) => !item.deletedAt
      );

      // Process items using the same recursive method as setItemsSupabase (proven to work)
      // Note: Using updateItemRecursively ensures items are correctly placed in nested categories
      // First, remove deleted items from state
      if (nonDeletedItems.length < items.length) {
        const deletedItemIds = items
          .filter((item: ISupabaseSpecificationItem) => item.deletedAt)
          .map((item: ISupabaseSpecificationItem) => item.id);

        // Remove deleted items recursively from categories using helper function
        deletedItemIds.forEach((itemId: string) => {
          state.categories = removeItemByIdFromAllCategories(
            state.categories,
            itemId
          );
        });
      }

      nonDeletedItems.forEach((itemResponse: ISupabaseSpecificationItem) => {
        state.categories = updateItemRecursively(
          state.categories,
          itemResponse
        );
      });

      // ✅ Keep filteredCategories same as categories - filtering happens in rendering
      state.filteredCategories = state.categories;
      const allItems = getAllItems(state.categories);
      state.allCategoryNames = getAllCategoryNames(state.categories);

      // state.currentItemIndex = 0;
      state.totalItems = allItems.length;
      state.totalSpecification = state.totalItems;
    },
    cleanSpecification: (state) => {
      Object.assign(state, initialState);
    },
    setSpecificationStatus: (state, action) => {
      state.specification.specification.status = action.payload;
    },
    setSpecificationType: (state, action) => {
      state.specification.specification.specType = action.payload;
    },
    setSpecificationCategoryNames: (state, action) => {
      state.specification.specification.categoryNames = action.payload;
    },
    removeMainCategory: (state, action) => {
      const { oldData, newData, categories } = action.payload;

      const oldCategory = categories.find(
        (category: ISpecificationCategoryResponse) => category.id === oldData.id
      );
      const existingCategories = categories.filter(
        (category: ISpecificationCategoryResponse) => category.id !== oldData.id
      );

      const newCategories = addCategoryToCategories(
        existingCategories,
        oldCategory,
        newData.parentId
      );

      const categoriesAction: PayloadAction<ISpecificationCategoryResponse[]> =
      {
        type: "specification/setCategories",
        payload: newCategories,
      };
      specificationSlice.caseReducers.setCategories(state, categoriesAction);
    },
    removeSubCategory: (state, action) => {
      const { oldData, newData, categories } = action.payload;

      const oldCategory = findCategoryById(categories, oldData.id);

      if (!oldCategory) return;

      const existingCategories = removeCategoryRecursively(
        categories,
        oldData.id
      );

      const newCategories = addCategoryToCategories(
        existingCategories,
        oldCategory,
        newData.parentId
      );

      const categoriesAction: PayloadAction<ISpecificationCategoryResponse[]> =
      {
        type: "specification/setCategories",
        payload: newCategories,
      };
      specificationSlice.caseReducers.setCategories(state, categoriesAction);
    },
    removeItemNewItem: (state, action) => {
      const { oldData, categories } = action.payload;

      const newCategories = removeItemRecursively(
        categories,
        oldData.category.id,
        oldData.id
      );

      const categoriesAction: PayloadAction<ISpecificationCategoryResponse[]> =
      {
        type: "specification/setCategories",
        payload: newCategories,
      };
      specificationSlice.caseReducers.setCategories(state, categoriesAction);
    },
    removeItem: (state, action) => {
      const { oldData, categories } = action.payload;

      const newCategories = removeItemRecursively(
        categories,
        oldData.categoryId,
        oldData.id
      );

      const categoriesAction: PayloadAction<ISpecificationCategoryResponse[]> =
      {
        type: "specification/setCategories",
        payload: newCategories,
      };
      specificationSlice.caseReducers.setCategories(state, categoriesAction);
    },
    setLoading: (state, action) => {
      state.specificationLoading = action.payload;
    },
    setInitialState: (state) => {
      Object.assign(state, initialState);
    },
    setIsSpecificationCreateSidebarOpen: (state, action) => {
      state.isSpecificationCreateSidebarOpen = action.payload;
    },
    setDashboardSidebarWidth: (state, action) => {
      state.dashboardSidebarWidth = action.payload;
    },
    setSidebarFilter: (state, action: PayloadAction<ISidebarFilter>) => {
      state.sidebarFilter = action.payload;
    },
    toggleSidebarFilterStatus: (state, action: PayloadAction<SpecificationItemAnswerStatus>) => {
      const status = action.payload;
      const idx = state.sidebarFilter.answerStatuses.indexOf(status);
      if (idx === -1) {
        state.sidebarFilter.answerStatuses.push(status);
      } else {
        state.sidebarFilter.answerStatuses.splice(idx, 1);
      }
    },
    toggleSidebarFilterConflicts: (state) => {
      state.sidebarFilter.showConflicts = !state.sidebarFilter.showConflicts;
    },
    clearSidebarFilter: (state) => {
      state.sidebarFilter = initialSidebarFilter;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(getSpecificationThunk.pending, (state) => {
        state.specificationLoading = true;
        state.specificationError = null;
      })
      .addCase(getSpecificationThunk.fulfilled, (state, action) => {
        state.specificationLoading = false;
        state.skeletonLoading = false;
        state.specification.specification = action.payload.specification;

        // Categories'i set et
        const categoriesAction: PayloadAction<
          ISpecificationCategoryResponse[]
        > = {
          type: "specification/setCategories",
          payload: action.payload.categories,
        };
        state.openCategories = action.payload.categories?.map(
          (category) => category.id
        );
        specificationSlice.caseReducers.setCategories(state, categoriesAction);

        // İlk kategoriyi seçili hale getir
        if (action.payload.categories && action.payload.categories.length > 0) {
          // Select first item automatically
          const allItems = getAllItems(action.payload.categories);
          if (allItems.length > 0 && !state.activeSpecificationStep.id) {
            const firstItem = allItems[0];
            const priority = getItemsPriority(action.payload.categories, firstItem);
            state.activeSpecificationStep = firstItem;
            state.activeSpecificationStep.priority = priority.priority;
            // Merge category path into openCategories (which already has root IDs)
            const openSet = new Set([
              ...state.openCategories,
              ...priority.categoryPath,
            ]);
            state.openCategories = Array.from(openSet);
            state.currentItemIndex = 1;
          } else if (!state.activeSpecificationStep.category) {
            state.activeSpecificationStep.category = { id: "" };
            state.activeSpecificationStep.category.id =
              action.payload.categories[0].id;
          }
        }
      })
      .addCase(getSpecificationThunk.rejected, (state, action) => {
        state.specificationLoading = false;
        state.specificationError = action.payload;
      })
      .addCase(getCategoryAndItemThunk.pending, (state, action) => {
        const searchTerm = action.meta?.arg?.search || "";
        if (searchTerm) {
          state.searchTerm = searchTerm;
        }
        state.specificationLoading = true;
        state.specificationError = null;
      })
      .addCase(getCategoryAndItemThunk.rejected, (state, action) => {
        setTimeout(() => {
          state.specificationLoading = false;
          state.specificationError = action.payload;
        }, 1000);
      })
      .addCase(getCategoryAndItemThunk.fulfilled, (state, action) => {
        state.specificationLoading = false;
        // setCategories'ı kullanarak tüm türetilmiş state'i güncelle
        const categoriesAction: PayloadAction<
          ISpecificationCategoryResponse[]
        > = {
          type: "specification/setCategories",
          payload: action.payload,
        };
        specificationSlice.caseReducers.setCategories(state, categoriesAction);
      })
      .addCase(getProductCategoriesThunk.pending, (state) => {
        state.productCategories.loading = true;
        state.productCategories.error = null;
      })
      .addCase(getProductCategoriesThunk.fulfilled, (state, action) => {
        state.productCategories.loading = false;
        state.productCategories.data = action.payload;
        state.productCategories.error = null;
      })
      .addCase(getProductCategoriesThunk.rejected, (state, action) => {
        state.productCategories.loading = false;
        state.productCategories.error = action.payload;
      });
  },
});

export const {
  setIsSpecificationCreateSidebarOpen,
  setDashboardSidebarWidth,
  setSpecification,
  setNameSpecification,
  setCategories,
  setactiveSpecificationStep,
  setActiveCategoryId,
  setActiveSpecificationItem,
  setActiveEmptySpecification,
  setOpenCategories,
  resetOpenCategories,
  setExpandedItems,
  setAllExpandedItems,
  resetExpandedItems,
  navigateItems,
  setSearchTerm,
  setOpenSidebar,
  setSpecificationDetail,
  setFilteredCategories,
  setMainAndSubCategoriesAndItems,
  setCurrentItemIndex,
  setMainAndSubCategories,
  setIsInputFocused,
  setChangedCategories,
  setChangedItems,
  cleanSpecification,
  setAllOpenCategories,
  setIsNewRequirementInputFocused,
  updateItem,
  setSpecificationStatus,
  setSpecificationType,
  setItemsSupabase,
  removeSubCategory,
  removeMainCategory,
  removeItem,
  removeItemNewItem,
  setAiPopover,
  setPlaceHolders,
  setSelectedPlaceHolder,
  setLoading,
  setInitialSpecification,
  addChangedItem,
  removeChangedItem,
  setInitialState,
  setActiveSpeciticationByItemId,
  setSpecificationCategoryNames,
  setOpenCategoriesCollapse,
  setThresHoldData,
  setActiveInitialSpecification,
  resetChangedValues,
  addHighlightedCategoryIds,
  removeHighlightedCategoryIds,
  clearHighlightedCategoryIds,
  expandCategoryIds,
  selectFirstItem,
  setSidebarFilter,
  toggleSidebarFilterStatus,
  toggleSidebarFilterConflicts,
  clearSidebarFilter,
} = specificationSlice.actions;
export default specificationSlice.reducer;
